import {
  IAnimalPreferenceData,
  IAnimalPreferenceResponse,
  IPreferenceType,
} from '@ninjacart/nc-nk-core';
import {
  addEventParam,
  INinjaEvent,
  useQueuedEventStore,
} from '@ninjacart/nc-events-sdk-v2';
import {
  animalListApi,
  createUserPreferenceApi,
  getUserPreferenceDetail,
  ICropPreference,
  KissanEventType,
  updateUserPreferenceApi,
  useAppPersistenceStore,
  useCropPreferenceStore,
  useGlobalLoginContext,
  useProfileStore,
} from '@ninjacart/nc-nk-store';
import { Button, CropsHomePlaceHolder, Text, View, XStack, YStack } from '@ninjacart/nc-nk-ui';
import { FlashList } from '@shopify/flash-list';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CropCard } from './cropTypeCard';

export const AnimalsListCard = ({ sourcePage }: { sourcePage: string }) => {
  const { t } = useTranslation();
  const [selectedAnimals, setSelectedAnimals] = useState<ICropPreference[]>([]);
  const [animalList, setAnimalList] = useState<IAnimalPreferenceData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const appLanguage = useAppPersistenceStore((state) => state.appLanguage);
  const userPreferenceId = useCropPreferenceStore((state) => state.userPreferenceId);
  const userProfile = useProfileStore((state) => state.userProfile);
  const setAnimalPreference = useCropPreferenceStore((state) => state.setAnimalPreference);
  const animalPreference = useCropPreferenceStore((state) => state.animalPreference);
  const cropPreference = useCropPreferenceStore((state) => state.cropPreference);
  const setUserPreferenceId = useCropPreferenceStore((state) => state.setUserPreferenceId);
  const hasUserLoggedIn = useProfileStore((state) => state.hasUserLoggedIn);
  const [saveButtonDisabled, setSaveButtonDisabled] = useState(true);
  const [initialSelectedAnimals, setInitialSelectedAnimals] = useState<ICropPreference[]>([]);
  const enqueue = useRef(useQueuedEventStore((state) => state.enqueue)).current;
  const fields = useGlobalLoginContext(['openLoginSheet','previousPage', 'navigationParams']);
  const setNavigationParams = fields.navigationParams.set;

  useEffect(() => {
    const fetchAndInitialize = async () => {
      await fetchAnimalList();
      if (hasUserLoggedIn()) {
        userCropPreferenceDetail();
      }
    };
    fetchAndInitialize();
  }, []);

  useEffect(() => {
    if (animalPreference?.length && animalList?.length)
      reOrderAnimalList(animalList, animalPreference);
  }, [animalPreference]);

  useEffect(() => {
    if (
      selectedAnimals?.length !== initialSelectedAnimals?.length ||
      selectedAnimals.some(
        (animal, index) => animal.domainId !== initialSelectedAnimals[index]?.domainId
      )
    ) {
      setSaveButtonDisabled(false);
    } else {
      setSaveButtonDisabled(true);
    }
  }, [selectedAnimals, initialSelectedAnimals]);

  useEffect(() => {
    fetchAnimalList();
  }, [appLanguage]);

  const fetchAnimalList = async () => {
    try {
      const animalListResponse: IAnimalPreferenceResponse = await animalListApi();
      setAnimalList(animalListResponse?.data);
    } catch (error) {
      console.error('Error fetching animal list:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const userCropPreferenceDetail = async () => {
    try {
      setIsLoading(true);
      const response = await getUserPreferenceDetail(IPreferenceType?.ANIMAL);
      if (response?.data?.animalPreference && response?.data?.animalPreference?.length > 0) {
        setAnimalPreference(response?.data?.animalPreference);
        prefillCropPreference(response?.data?.animalPreference);
      }
    } catch (error) {
      console.error('Error fetching user animal preference:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const prefillCropPreference = useMemo(
    () => (preferenceData) => {
      setSelectedAnimals([...preferenceData]);
      setInitialSelectedAnimals([...preferenceData]);
      //  reOrderAnimalList();
    },
    [animalPreference]
  );

  const handleAnimalCardSelectEvent = (event, animal) => {
    const animalNames = selectedAnimals?.length
      ? Array.from(new Set(selectedAnimals?.map((animal) => animal?.name))).join(',')
      : '';
    const animalCardSelectEvent: INinjaEvent = {
      name: event,
      screenDetails: { screen: 'animals card page' },
      eventParams: [
        addEventParam('selectedAnimal', animal),
        addEventParam('animalPreference', animalNames),
      ],
    };
    enqueue(animalCardSelectEvent);
  };

  const handleAnimalCardRemoveEvent = (animal) => {
    const animalNames = selectedAnimals?.length
      ? Array.from(new Set(selectedAnimals?.map((animal) => animal?.name))).join(',')
      : '';
    const animalRemoveEvent: INinjaEvent = {
      name: KissanEventType.ANIMAL_REMOVE,
      screenDetails: { screen: 'animals card page' },
      eventParams: [
        addEventParam('removedAnimal', animal),
        addEventParam('animalPreference', animalNames),
      ],
    };
    enqueue(animalRemoveEvent);
  };

  const toggleSelectItem = useCallback(
    (item) => {
      const event =
        sourcePage === 'Feed'
          ? KissanEventType?.FEED_USER_ANIMALSELECT
          : KissanEventType.PROFILE_USER_ANIMALSELECT;

      const itemIndex = selectedAnimals?.findIndex(
        (selectedItem) => selectedItem?.domainId === item?.id
      );

      if (itemIndex > -1) {
        handleAnimalCardRemoveEvent(item?.name);
      } else {
        handleAnimalCardSelectEvent(event, item?.name);
      }

      if (!hasUserLoggedIn()) {
        fields['openLoginSheet'].set(true);
        fields['previousPage'].set(`${sourcePage} Page`);
        setNavigationParams({
          name: '',
          routeName: {
            name: '(tabs)',
            state: {
              routes: [
                {
                  name: 'profileDetail',
                  params: { id: userProfile?.user.id },
                },
              ],
            },
          },
          description: `${t('page.loginToSelectAnimal')}`,
          screenName: `${sourcePage} Page`,
        });
        return;
      }

      const updatedSelectedAnimals =
        itemIndex > -1
          ? selectedAnimals?.filter((_, index) => index !== itemIndex)
          : [
            ...selectedAnimals,
            {
              name: item?.name,
              domainId: item?.id,
              type: 'AnimalHusbandry',
              isSubjectMatterExpert: true,
              priority: 'Primary',
            },
          ];

      setSelectedAnimals(updatedSelectedAnimals);
    },
    [selectedAnimals]
  );

  const handleAnimalCardSaveEvent = () => {
    const animalRemoveEvent: INinjaEvent = {
      name: KissanEventType.ANIMAL_ADDED,
      screenDetails: { screen: 'animals card page' },
      eventParams: [addEventParam('selectedAnimal', JSON.stringify(selectedAnimals))],
    };
    enqueue(animalRemoveEvent);
  };

  const handlePreferenceChange = async () => {
    handleAnimalCardSaveEvent();
    setIsLoading(true);
    try {
      const animalPreferencesData = {
        animalPreference: [...selectedAnimals],
      };

      const inputWithExistingPreference = {
        input: {
          id: userPreferenceId,
          node: 'animalPreference',
          data: animalPreferencesData,
        },
      };

      const inputWithNewPreference = {
        input: {
          UserPreference: {
            realmId: userProfile?.realm?.id,
            userId: Number(userProfile?.user?.id),
            ...animalPreferencesData,
          },
        },
      };

      const config: any = {
        realmId: userProfile?.realm?.id,
        userId: userProfile?.user?.id,
        formData:
          cropPreference?.length > 0 || animalPreference?.length > 0
            ? inputWithExistingPreference
            : inputWithNewPreference,
      };

      let response: any;

      if (cropPreference?.length > 0 || animalPreference?.length > 0) {
        response = await updateUserPreferenceApi(config);
      } else {
        response = await createUserPreferenceApi(config);
      }

      if (response?.data?.success) {
        const preferenceData =
          cropPreference?.length > 0 || animalPreference?.length > 0
            ? response?.data?.data?.UpdatedUserPreferenceData?.UserPreference
            : response?.data?.data?.CreatedUserData?.UserPreference;

        setAnimalPreference(preferenceData?.animalPreference);
        setUserPreferenceId(preferenceData?.id);
        prefillCropPreference(preferenceData?.animalPreference);
      }
    } catch (error) {
      console.error('Error saving preferences:', error);
    } finally {
      setIsLoading(false);
      setSaveButtonDisabled(true);
    }
  };

  const reOrderAnimalList = (list, preference) => {
    // console.log('rendereded');

    const reorderedAnimalList = list.sort((a, b) => {
      const isASelected = preference.some((selected) => selected.domainId === a.id);
      const isBSelected = preference.some((selected) => selected.domainId === b.id);
      return isASelected === isBSelected ? 0 : isASelected ? -1 : 1;
    });
    setAnimalList(reorderedAnimalList);
  };

  const renderAnimalItem = ({ item,index }) => (
    <CropCard
      crop={item}
      isSelected={selectedAnimals?.some((selectedItem) => selectedItem?.domainId === item?.id)}
      onSelect={() => toggleSelectItem(item)}
      mediaUrl={item?.mediaUrl}
      regionalName={item?.languageName ?? item?.name}
      type={IPreferenceType.ANIMAL}
      index={index}
    />
  );

  return isLoading ? (
    <CropsHomePlaceHolder />
  ) : (
    <YStack padding="$4" marginBottom={4} minHeight={120}>
      <XStack>
        <YStack>
          <Text fontSize={12} fontWeight="400" paddingBottom={6} color="$grey">
            {t('page.animalsYouRaise')}
          </Text>
          <Text fontSize={14} fontWeight="500" paddingBottom={6} color="$color">
            {t('page.helpAnimalInformations')}
          </Text>
        </YStack>
      </XStack>

      <View flex={1} marginTop="$4" marginBottom={4}>
        <FlashList
          data={animalList}
          renderItem={renderAnimalItem}
          keyExtractor={(item, index) => index.toString()}
          horizontal
          showsHorizontalScrollIndicator={false}
          estimatedItemSize={100}
          extraData={selectedAnimals}
        />
      </View>

      {!saveButtonDisabled && (
        <XStack justifyContent="center" alignItems="center" marginTop={10}>
          <Button
            width={140}
            backgroundColor="$primary"
            paddingVertical="$1"
            paddingHorizontal="$4"
            borderRadius="$6"
            onPress={handlePreferenceChange}
            testID="kisan_tId_saveAnimalPreference"
          >
            <Text color="$light" fontWeight="500">
              {t('page.save')}
            </Text>
          </Button>
        </XStack>
      )}
    </YStack>
  );
};
